/**
 * M9 CH-10: fixing a claimed hull's flaws at a yard (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §2, §4.4).
 *
 * - **One flaw at a time**, at a station with a yard, paid in goods from the hold: CH-4's `flawFixCost(hull, i)`
 *   (a share 0.36–0.40 of the hull value per flaw, in whole units of plating, drive parts or electronics). No credits
 *   move, so there is nothing to settle twice (plan §2): the goods leave the Career once, when the fix is written.
 * - **The quote** (`flawFixQuote`) names the goods, the bitmask after the fix (`withoutFlaw`: the First Rush edge,
 *   bits 12–15, stays) and its insured value factor (CH-8b, D-572: `insure.ts insuredValueMult`, the entry's factor
 *   raised by `putInShare` × the fix's goods at base price, never above `valueMultOf` of the new bitmask). A ship whose
 *   factor follows its flaws (absent = 1) gets `valueMultOf`, so the last flaw gives 1 and the entry turns pristine;
 *   a claimed ship insured at what was put in keeps a factor below 1 until its fixes have paid for the hull.
 * - **The fix** (`fixFlaw`) is Career-in, Career-out: refused with no write when the ship isn't in the hangar
 *   (`noShip`; the lease ship never has flaws), the flaw isn't on it (`noFlaw`), the station has no yard
 *   (`notYard`), or the hold lacks a good (`goods`, with what is missing). On success the goods leave
 *   `cargo.parts` and the entry's `flaws`/`valueMult` are rewritten (absent when pristine, CH-8's rule); a flown
 *   ship re-derives from its entry (CH-9a). The result carries the worker's `setHullFlaws` command.
 * - **The worker side** goes through CH-7s's `fixHullFlaw` in the D-394 order: `fixFlaw` on the current Career as a
 *   check, send `result.command` (the worker re-checks the yard and that the ship is docked there or boarded), then
 *   on success write `fixFlaw` again on the latest Career. A refused worker command writes nothing.
 * - **Hull points:** a fixed `hull` flaw raises the ship's full hull; the hull points it has stay as they were (a
 *   recorded `OwnedShip.hull`, or the flown ship's), and the repair yard tops them up.
 *
 * `place` is the station the player is docked at (their hold pays) and whether its strategic view has a yard (the
 * UI's `hasShipyard`). Pure; UI-rate, never per frame.
 */
import { EPSILON, withGood } from '../career/ops'
import { goodT, type GoodId, type Goods } from '../career/parts'
import type { Career, OwnedShip } from '../career/types'
import hullsJson from '../data/strategic/hulls.json'
import { OWN_SHIP_ID, ownedShip, updateOwnedShip } from '../fleet-link/hangar'
import type { SetHullFlawsCommand } from '../strategic/types'
import { hulksConfig, MAX_FLAWS, type HulksConfig } from './data'
import { flawFixCost, goodsValue, withoutFlaw } from './flaws'
import { insuredValueMult } from './insure'

/** The strategic hull of each ship def (`hulls.json shipDef` is one-to-one, e.g. `kestrel` → `light_fighter`). */
const HULL_OF_DEF: ReadonlyMap<string, string> = new Map(hullsJson.hulls.map((h) => [h.shipDef, h.id]))

/** The strategic hull id of ship def `defId`, or null. */
export function hullOfDef(defId: string): string | null {
  return HULL_OF_DEF.get(defId) ?? null
}

export type FlawFixRefusal =
  /** No hangar entry for the fleet id, or the lease ship. */
  | 'noShip'
  /** The flaw is not on the ship (or the index is not a flaw; the FR edge is never "fixed"). */
  | 'noFlaw'
  /** The ship's def has no strategic hull, so no fix cost. */
  | 'unknownHull'
  /** The station has no yard. */
  | 'notYard'
  /** The hold lacks some of the goods (`missing`). */
  | 'goods'

/** Plain words for each refusal (CH-12b's disabled *Fix flaw* button). */
export const FLAW_FIX_REFUSAL_TEXT: Readonly<Record<FlawFixRefusal, string>> = Object.freeze({
  noShip: 'That ship is not in your hangar.',
  noFlaw: 'That ship has no such flaw.',
  unknownHull: 'No yard can work on that hull.',
  notYard: 'Only a station with a yard can fix a flaw.',
  goods: 'Your hold lacks the goods for this fix.',
})

export interface FlawFixQuote {
  /** The flaw's list index (`hulks.json flaws[i]`, bit i). */
  readonly flaw: number
  readonly name: string
  /** The goods the fix takes from the hold, in whole units. */
  readonly goods: Readonly<Goods>
  /** The bitmask after the fix (the edge bits kept). */
  readonly flaws: number
  /** The insured value factor after the fix (CH-8b: never above what was put in; 1 once a flaw-valued hull is pristine). */
  readonly valueMult: number
}

export type FlawFixQuoteResult = { ok: true; quote: FlawFixQuote } | { ok: false; reason: 'noFlaw' | 'unknownHull' }

/** Where the fix happens: the station the player is docked at, and whether it has a yard. */
export interface FlawFixPlace {
  readonly stationId: string
  readonly yard: boolean
}

export type FlawFixResult =
  | {
      ok: true
      career: Career
      quote: FlawFixQuote
      /** The worker command that mirrors the fix (CH-7s sends it; `setHullFlaws`, CH-7a). */
      command: SetHullFlawsCommand
    }
  | { ok: false; reason: FlawFixRefusal; missing?: Goods }

/** The quote to fix flaw `flaw` on a ship (its def, current bitmask and insured value factor; absent = 1). */
export function flawFixQuote(ship: Pick<OwnedShip, 'defId' | 'flaws' | 'valueMult'>, flaw: number, cfg: HulksConfig = hulksConfig()): FlawFixQuoteResult {
  const bits = ship.flaws ?? 0
  if (!Number.isInteger(flaw) || flaw < 0 || flaw >= MAX_FLAWS || flaw >= cfg.flaws.length || (bits & (1 << flaw)) === 0) {
    return { ok: false, reason: 'noFlaw' }
  }
  const hull = hullOfDef(ship.defId)
  if (hull === null) return { ok: false, reason: 'unknownHull' }
  const goods = flawFixCost(hull, flaw, cfg)
  if (Object.keys(goods).length === 0) return { ok: false, reason: 'unknownHull' }
  const flaws = withoutFlaw(bits, flaw)
  // CH-8b: the fix's goods at base price are what this fix puts in.
  const valueMult = insuredValueMult(hull, flaws, ship.valueMult ?? 1, goodsValue(goods), cfg)
  return { ok: true, quote: { flaw, name: cfg.flaws[flaw].name, goods, flaws, valueMult } }
}

/** The goods of `goods` the hold lacks (empty when it carries them all). */
export function flawFixMissing(career: Career, goods: Readonly<Goods>): Goods {
  const out: Goods = {}
  for (const [g, n] of Object.entries(goods) as [GoodId, number | undefined][]) {
    if (n === undefined || n <= 0) continue
    const held = goodT(career.cargo.parts, g)
    if (held + EPSILON < n) out[g] = n - held
  }
  return out
}

/** The entry with the fixed condition (only non-pristine fields kept, CH-8's rule). */
function withCondition(entry: OwnedShip, flaws: number, valueMult: number): OwnedShip {
  const out: OwnedShip = { ...entry }
  delete out.flaws
  delete out.valueMult
  if (flaws !== 0) out.flaws = flaws
  if (valueMult !== 1) out.valueMult = valueMult
  return out
}

/**
 * Fixes flaw `flaw` on hangar ship `fleetId` at `place`: the goods leave the hold and the entry's condition is
 * rewritten. Refusals write nothing.
 */
export function fixFlaw(career: Career, fleetId: string, flaw: number, place: FlawFixPlace, cfg: HulksConfig = hulksConfig()): FlawFixResult {
  const entry = fleetId === OWN_SHIP_ID ? null : ownedShip(career, fleetId)
  if (entry === null) return { ok: false, reason: 'noShip' }
  const q = flawFixQuote(entry, flaw, cfg)
  if (!q.ok) return { ok: false, reason: q.reason }
  if (!place.yard) return { ok: false, reason: 'notYard' }
  const quote = q.quote
  const missing = flawFixMissing(career, quote.goods)
  if (Object.keys(missing).length > 0) return { ok: false, reason: 'goods', missing }

  let next = career
  for (const [g, n] of Object.entries(quote.goods) as [GoodId, number | undefined][]) {
    if (n === undefined || n <= 0) continue
    next = withGood(next, g, -Math.min(n, goodT(next.cargo.parts, g)))
  }
  next = updateOwnedShip(next, fleetId, (e) => withCondition(e, quote.flaws, quote.valueMult))
  const command: SetHullFlawsCommand = { type: 'setHullFlaws', shipId: fleetId, flaws: quote.flaws, valueMult: quote.valueMult, stationId: place.stationId }
  return { ok: true, career: next, quote, command }
}
